import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/base/ScrollReveal';

const trustPillars = [
  {
    icon: 'ri-shield-check-line',
    title: '316L Stainless Steel',
    subtitle: 'Surgical-Grade Base',
    description:
      'We start with 316L — the same grade used in medical implants. It\'s hypoallergenic, corrosion-resistant, and virtually indestructible under normal wear. No brass, no copper alloys, no shortcuts.',
  },
  {
    icon: 'ri-sun-line',
    title: 'PVD Gold-Tone Plating',
    subtitle: 'Molecular-Level Bond',
    description:
      'Physical Vapor Deposition bonds gold-tone particles directly to the steel at the molecular level — not painted or dipped on top. Result: a finish that doesn\'t rub off, flake, or fade with daily wear.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Nickel-Free & Hypoallergenic',
    subtitle: 'Safe for Sensitive Skin',
    description:
      'Every piece is lab-tested to confirm nickel release below EU REACH thresholds. No lead, no cadmium, no mystery metals. Just pure, irritation-free comfort — even if you wear it 24/7.',
  },
  {
    icon: 'ri-gift-line',
    title: 'Premium Packaging',
    subtitle: 'Gift-Ready, Every Time',
    description:
      'Every order arrives in a magnetic-close gift box with a soft anti-tarnish pouch inside. Unboxing feels like an event — whether it\'s a gift for someone else or a treat for yourself.',
  },
];

const whatMakesUsDifferent = [
  {
    icon: 'ri-flashlight-line',
    title: 'New Drops Every 4-6 Weeks',
    description:
      'We don\'t do seasons. We watch what\'s trending globally and design fast — so your jewellery always feels fresh, not like last year\'s leftovers.',
  },
  {
    icon: 'ri-currency-line',
    title: 'Direct-to-You Pricing',
    description:
      'No retail markup, no distributor margins. You pay for the product and the craft — not a mall rental in a fancy neighbourhood.',
  },
  {
    icon: 'ri-recycle-line',
    title: 'Designed to Last, Not Replace',
    description:
      'We design against the "buy cheap, replace often" cycle. Every piece is engineered for years of wear — better for your wallet and the planet.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Real Humans, Real Support',
    description:
      'Got a question about sizing, materials, or care? Reach us on WhatsApp. No chatbots, no ticket systems — just actual people who love jewellery.',
  },
];

const designedFor = [
  {
    icon: 'ri-run-line',
    title: 'The Commuter',
    desc: 'Sweat through rush hour, rain on the way to the metro — your gold stays put and stays gold.',
  },
  {
    icon: 'ri-briefcase-line',
    title: 'The Professional',
    desc: 'Boardroom to bar — one set of earrings, zero touch-ups. Polished enough for meetings, tough enough for after-hours.',
  },
  {
    icon: 'ri-book-open-line',
    title: 'The Student',
    desc: 'Campus, coffee runs, library marathons. Jewellery that keeps up with your schedule, not the other way around.',
  },
  {
    icon: 'ri-sparkling-line',
    title: 'The Celebrator',
    desc: 'Weddings, festivals, date nights — show up looking intentional without stressing about your jewellery surviving the night.',
  },
];

export default function WhyJoviaaPage() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (video.currentTime >= 7) {
        video.currentTime = 0;
        video.play();
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      {/* Hero Section */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            src="https://storage.readdy-site.link/project_files/4ae51fa3-40da-40a1-b133-2a5cd9cb4858/9b065551-604a-4e59-bfc7-90138452fa45_jovia-4.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-28 flex flex-col items-center text-center">
          <p className="text-accent-400 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-4">
            The Joviaa Difference
          </p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 max-w-3xl leading-tight">
            Gold That Actually<br />Keeps Up With You.
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-background-50/80 font-label max-w-2xl leading-relaxed">
            Most gold-tone jewellery looks good for a few weeks, then it's done. We built Joviaa for women who refuse to choose between style and durability — and here's exactly how we pull it off.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="relative overflow-hidden py-14 md:py-20 bg-primary-500">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(0,0,0,0.25)_100%)]"></div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
          <ScrollReveal>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-background-50 mb-6">
              Our Story, in 30 Seconds
            </h2>
            <p className="text-sm md:text-base text-background-50/70 font-label leading-relaxed max-w-xl mx-auto">
              We got tired of jewellery that looked beautiful in the box but didn&apos;t survive real life. So we found a better way — starting with the materials, not the markup.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* The Four Pillars */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <ScrollReveal>
          <div className="text-center mb-10 md:mb-14">
            <p className="text-foreground-400 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-3">
              Built Different
            </p>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-primary-500">
              What Goes Into Every Piece
            </h2>
            <p className="mt-3 text-foreground-600 font-label text-sm md:text-base max-w-2xl mx-auto">
              Four choices that change everything — from the metal we pick to the box it arrives in.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {trustPillars.map((pillar, idx) => (
            <ScrollReveal key={pillar.title} delay={idx * 100}>
              <div className="bg-background-50 rounded-2xl border border-background-200/60 p-6 md:p-8 flex gap-5 group hover:border-accent-200 transition-colors">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${pillar.icon} text-xl md:text-2xl text-accent-600`}></i>
                </div>
                <div>
                  <p className="text-[11px] font-label font-semibold uppercase tracking-wider text-accent-500 mb-1">
                    {pillar.subtitle}
                  </p>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-primary-500 mb-2">
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-foreground-600 font-label leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="bg-secondary-100 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-14">
              <p className="text-secondary-500 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-3">
                Beyond Materials
              </p>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-primary-500">
                What Makes Us Different
              </h2>
              <p className="mt-3 text-foreground-600 font-label text-sm md:text-base max-w-2xl mx-auto">
                Great materials are just the start. Here's everything else that sets Joviaa apart from mass-market jewellery.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {whatMakesUsDifferent.map((item, idx) => (
              <ScrollReveal key={item.title} delay={idx * 100}>
                <div className="bg-background-50 rounded-xl p-5 md:p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${item.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-sm md:text-base font-heading font-semibold text-primary-500 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 font-label leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <ScrollReveal>
        <section className="bg-primary-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50">
                Ready to experience the difference?
              </h2>
              <p className="mt-2 text-sm md:text-base text-background-50/70 font-label">
                Shop our collection — every piece backed by everything you just read.
              </p>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0 whitespace-nowrap flex-wrap">
              <Link
                to="/shop"
                className="px-7 py-3 bg-accent-500 text-primary-500 rounded-lg font-heading font-semibold text-sm hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                Shop Now
              </Link>
              <Link
                to="/durability"
                className="px-7 py-3 border-2 border-background-50/30 text-background-50 rounded-lg font-heading font-semibold text-sm hover:border-background-50/60 transition-colors cursor-pointer whitespace-nowrap"
              >
                See Our Tests
              </Link>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Designed For Real Life */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <ScrollReveal>
          <div className="text-center mb-10 md:mb-14">
            <p className="text-foreground-400 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-3">
              For Every Version of You
            </p>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-primary-500">
              Designed for Real Life
            </h2>
            <p className="mt-3 text-foreground-600 font-label text-sm md:text-base max-w-2xl mx-auto">
              We design for women who don't compartmentalise their lives — because your jewellery shouldn't either.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {designedFor.map((persona, idx) => (
            <ScrollReveal key={persona.title} delay={idx * 100}>
              <div className="bg-background-50 rounded-xl border border-background-200/60 p-5 md:p-6 text-center group hover:border-accent-200 transition-colors">
                <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <i className={`${persona.icon} text-xl text-accent-600`}></i>
                </div>
                <h3 className="text-sm md:text-base font-heading font-semibold text-primary-500 mb-2">
                  {persona.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 font-label leading-relaxed">
                  {persona.desc}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>
    </main>
  );
}